import { breakpointSizes, getBreakpointDown } from '@paywithextend/components'
import {
  base_colorAction,
  base_colorActionHover,
  base_colorContentBorder,
  base_colorRaisedSurface,
  base_contentActive,
  tag_colorTagDefaultBackground,
  tag_colorTagPendingBackground,
  tag_colorTagPendingTextHover,
  text_colorTextPrimary,
  text_colorTextSecondary,
} from 'ext-ui/style/colors'
import { spacing } from 'ext-ui/style/spacing'
import {
  font_2xl,
  font_family_weight_medium,
  font_lg,
  font_md,
  font_sm,
  font_xs,
} from 'ext-ui/style/typography'
import { css } from 'styled-components'

export const overview = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing(4)};

  h2,
  h3,
  p,
  ul {
    margin: 0;
  }

  ul {
    list-style: none;
    padding: 0;
  }

  .overviewCard.Box {
    background: ${base_colorRaisedSurface};
    border: 1px solid ${base_colorContentBorder};
    border-radius: ${spacing(4)};
  }

  #current-responsibility-heading {
    margin-top: ${spacing(6)};
  }

  .responsibilityHeader {
    align-items: flex-start;
    display: flex;
    gap: ${spacing(3)};
    justify-content: space-between;
    margin-bottom: ${spacing(3)};

    h2 {
      color: ${text_colorTextPrimary};
      font-family: ${font_family_weight_medium};
      ${font_lg};
      margin-bottom: ${spacing(1)};
    }

    p {
      color: ${text_colorTextSecondary};
      ${font_sm};
      line-height: 18px;
    }
  }

  .viewAllWork {
    color: ${base_colorAction};
    cursor: pointer;
    flex: 0 0 auto;
    font-family: ${font_family_weight_medium};
    ${font_sm};
    text-decoration: none;

    &:hover {
      color: ${base_colorActionHover};
      text-decoration: underline;
    }

    &:focus-visible {
      border-radius: ${spacing(1)};
      outline: 2px solid ${base_colorAction};
      outline-offset: ${spacing(1)};
    }
  }

  .linkButton {
    appearance: none;
    background: transparent;
    border: 0;
    color: ${base_colorAction};
    cursor: pointer;
    font-family: ${font_family_weight_medium};
    ${font_sm};
    min-height: ${spacing(8)};
    padding: ${spacing(1)};
    text-align: left;

    &:hover {
      color: ${base_colorActionHover};
      text-decoration: underline;
    }

    &:focus-visible {
      border-radius: ${spacing(1)};
      outline: 2px solid ${base_colorAction};
      outline-offset: ${spacing(1)};
    }
  }

  .responsibilityGrid {
    display: grid;
    gap: ${spacing(3)};
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .responsibilityTile {
    border: 1px solid ${base_colorContentBorder};
    border-radius: ${spacing(2)};
    display: flex;
    flex-direction: column;
    gap: ${spacing(1)};
    height: ${spacing(22)};
    min-width: 0;
    padding: ${spacing(3)};

    strong {
      color: ${text_colorTextPrimary};
      font-family: ${font_family_weight_medium};
      ${font_2xl};
    }

    strong[data-state='needs-review'] {
      color: ${tag_colorTagPendingTextHover};
    }
  }

  .responsibilityTileButton {
    appearance: none;
    background: ${base_colorRaisedSurface};
    cursor: pointer;
    font: inherit;
    text-align: left;
    width: 100%;

    &:hover {
      border-color: ${base_colorAction};
    }

    &:focus-visible {
      outline: 2px solid ${base_colorAction};
      outline-offset: ${spacing(1)};
    }
  }

  .tileLabelRow {
    align-items: flex-start;
    color: ${text_colorTextSecondary};
    display: flex;
    gap: ${spacing(2)};
    height: ${spacing(9)};
    justify-content: space-between;
    ${font_sm};

    .currentResponsibilityIcon {
      display: block;
      flex: 0 0 ${spacing(6)};
      height: ${spacing(4)};
      position: relative;
      width: ${spacing(4)};

      img {
        display: block;
        height: ${spacing(4)};
        left: ${spacing(1)};
        position: absolute;
        top: ${spacing(1)};
        width: ${spacing(4)};
      }
    }
  }

  .responsibilityEquation {
    color: ${text_colorTextSecondary};
    ${font_xs};
    margin-top: ${spacing(3)};

    &::first-line {
      color: ${text_colorTextPrimary};
    }
  }

  .overviewColumns {
    display: grid;
    gap: ${spacing(4)};
    grid-template-columns: minmax(0, ${spacing(85)}) minmax(0, 1fr);
  }

  .sectionLabel {
    color: ${text_colorTextSecondary};
    font-family: ${font_family_weight_medium};
    ${font_sm};
    margin-bottom: ${spacing(2)};
  }

  .periodCard,
  .queueCard {
    overflow: hidden;
  }

  .periodRow {
    border-bottom: 1px solid ${base_colorContentBorder};
    min-height: ${spacing(16)};

    &:last-child {
      border-bottom: 0;
    }

    &[data-selected='true'] {
      background: ${base_contentActive};
    }
  }

  .periodButton {
    align-items: center;
    appearance: none;
    background: transparent;
    border: 0;
    color: inherit;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    min-height: ${spacing(16)};
    padding: ${spacing(3)} ${spacing(4)};
    text-align: left;
    width: 100%;

    &:hover {
      background: ${base_contentActive};
    }

    &:focus-visible {
      outline: 2px solid ${base_colorAction};
      outline-offset: -2px;
    }

    div {
      display: flex;
      flex-direction: column;
      gap: ${spacing(1)};
    }

    strong {
      color: ${text_colorTextPrimary};
      font-family: ${font_family_weight_medium};
      ${font_md};
    }

    span {
      color: ${text_colorTextSecondary};
      ${font_sm};
    }
  }

  .periodChevron {
    height: ${spacing(4)};
    width: ${spacing(4)};
  }

  .queueHeader {
    padding: ${spacing(5)} ${spacing(5)} ${spacing(2)};

    h3 {
      color: ${text_colorTextPrimary};
      font-family: ${font_family_weight_medium};
      ${font_lg};
      margin-bottom: ${spacing(1)};
    }

    p {
      color: ${text_colorTextSecondary};
      ${font_sm};
      line-height: 18px;
    }
  }

  .queueSignal {
    align-items: flex-start;
    display: flex;
    gap: ${spacing(3)};
    margin: 0 ${spacing(5)};
    padding: ${spacing(3)} 0;

    &:not(:last-child) {
      border-bottom: 1px solid ${base_colorContentBorder};
    }
  }

  .queueIcon {
    align-items: center;
    background: ${tag_colorTagDefaultBackground};
    border-radius: ${spacing(2)};
    display: flex;
    flex: 0 0 ${spacing(6)};
    height: ${spacing(6)};
    justify-content: center;

    .interventionQueueIcon {
      display: block;
      height: ${spacing(6)};
      position: relative;
      width: ${spacing(6)};

      img {
        display: block;
        position: absolute;
      }

      &--needs-review img,
      &--waiting-on-spender img {
        height: 14px;
        left: ${spacing(1.25)};
        top: ${spacing(1.25)};
        width: 14px;
      }
    }
  }

  .queueSignal[data-state='needs-review'] .queueIcon {
    background: ${tag_colorTagPendingBackground};
  }

  .queueCopy {
    align-items: flex-start;
    display: flex;
    flex: 1;
    flex-direction: column;
    min-width: 0;

    strong {
      color: ${text_colorTextPrimary};
      font-family: ${font_family_weight_medium};
      ${font_sm};
    }

    span {
      color: ${text_colorTextSecondary};
      ${font_sm};
      margin-top: ${spacing(1)};
    }
  }

  .queueSignal .linkButton {
    align-self: flex-start;
    flex: 0 0 auto;
    margin-left: auto;
    min-height: auto;
    padding: 0;
    white-space: nowrap;
  }

  ${getBreakpointDown(breakpointSizes.SM)(css`
    .overviewColumns {
      grid-template-columns: minmax(0, 1fr);
    }
  `)}

  ${getBreakpointDown(breakpointSizes.XSM)(css`
    .responsibilityGrid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  `)}

  ${getBreakpointDown(breakpointSizes.XXSM)(css`
    .responsibilityHeader {
      align-items: flex-start;
      flex-direction: column;
    }

    .responsibilityGrid {
      grid-template-columns: minmax(0, 1fr);
    }
  `)}
`
